<template>
    <div class="permissions card px-8 pt-5 pb-10 white rounded-lg">
        <h3 class="page-section-title mb-4">Permissions</h3>

        <template v-if="isLoadingPermissions">
            <div v-for="i in 2" :key="i">
                <v-skeleton-loader type="card-heading"></v-skeleton-loader>
                <v-skeleton-loader type="list-item-three-line"></v-skeleton-loader>
            </div>
        </template>
        <template v-else>
            <div v-if="hasPermissions" :class="{ 'view disabled-view': isView }">
                <div v-for="(permission, i) in groupedPermissions" :key="i">
                    <div v-for="(value, groupName) in permission" :key="groupName">
                        <v-expansion-panels v-model="panel[groupName]" class="group-panel my-2 d-block" multiple flat inset>
                            <v-expansion-panel>
                                <v-expansion-panel-header class="group-panel-header d-flex align-center">
                                    <v-layout class="align-center justify-space-between">
                                        <span class="group-label">
                                            <v-checkbox
                                                v-model="selectedGroups"
                                                :value="groupName"
                                                :label="groupName"
                                                color="success"
                                                :disabled="isView"
                                                hide-details
                                                @click.native.stop="toggleGroup(groupName)"
                                            ></v-checkbox>
                                        </span>
                                        <span class="ml-3 primary--text"> {{ getCount(groupName) }}</span>
                                        <template v-slot:actions>
                                            <v-icon> mdi-chevron-down </v-icon>
                                        </template>
                                    </v-layout>
                                </v-expansion-panel-header>
                                <v-expansion-panel-content class="mt-1 mb-4">
                                    <div v-for="(sub_group, j) in value" :key="j">
                                        <div v-for="(value, subGroupName) in sub_group" :key="subGroupName">
                                            <v-expansion-panels v-model="panel[groupName + '-' + subGroupName]" :class="['subgroup-panel d-block', { 'disabled-view': action == 'view' }]" multiple flat inset>
                                                <v-expansion-panel>
                                                    <v-expansion-panel-header class="subgroup-panel-header py-2 d-flex align-center">
                                                        <v-layout class="align-center justify-space-between">
                                                            <span class="subgroup-label">
                                                                <v-checkbox
                                                                    v-model="selectedSubGroups"
                                                                    :value="groupName + '-' + subGroupName"
                                                                    :label="subGroupName"
                                                                    color="success"
                                                                    :disabled="isView"
                                                                    hide-details
                                                                    @click.native.stop="toggleSubGroup(groupName, subGroupName)"
                                                                ></v-checkbox>
                                                            </span>
                                                            <span class="ml-3 primary--text">{{ getCount(groupName + '-' + subGroupName) }}</span>
                                                            <template v-slot:actions>
                                                                <v-icon> mdi-chevron-down </v-icon>
                                                            </template>
                                                        </v-layout>
                                                    </v-expansion-panel-header>
                                                    <v-expansion-panel-content>
                                                        <ul>
                                                            <li v-for="(sub_group, i) in value" :key="i">
                                                                <span class="actionkey-label">
                                                                    <v-checkbox
                                                                        v-model="selectedActionKeys"
                                                                        :label="sub_group.text"
                                                                        :value="sub_group.value"
                                                                        hide-details
                                                                        color="success"
                                                                        :disabled="isView"
                                                                        @click.native.stop="onCheckActionKey(groupName, subGroupName, sub_group.value)"
                                                                    ></v-checkbox>
                                                                </span>
                                                            </li>
                                                        </ul>
                                                    </v-expansion-panel-content>
                                                </v-expansion-panel>
                                            </v-expansion-panels>
                                        </div>
                                    </div>
                                </v-expansion-panel-content>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </div>
                </div>
            </div>
            <div v-else>
                <i>No permissions to show</i>
            </div>
        </template>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name: 'UserAccessPermissions',
    props: {
        reload: Boolean,
        action: String,
        page: String,
        roleId: String,
        rolePermissions: String
    },
    data() {
        return {
            isLoadingPermissions: true,
            localModuleCount: [],
            selectedGroups: [],
            selectedSubGroups: [],
            panel: []
        };
    },
    created() {
        if (this.$route.params.action == 'add') {
            this.$store.commit('permissions/SET_USER_ACTION_KEYS', []);

            if (this.page == 'roles') {
                this.getPermissions();
            }
        }
    },
    computed: {
        ...mapGetters({
            permissions: 'permissions/getPermissions',
            groupedPermissions: 'permissions/getGroupedPermissions'
        }),
        hasPermissions() {
            return this.groupedPermissions && Object.keys(this.groupedPermissions).length;
        },
        isView() {
            return this.$route.params.action == 'view' || this.page == 'users';
        },
        moduleCount() {
            return this.$store.getters['permissions/getModuleCount'];
        },
        userActionKeys() {
            return this.$store.getters['permissions/getUserActionKeys'];
        }
    },
    watch: {
        $route(to, from) {
            this.from = from.name;
            if (from.params.action == 'edit') {
                this.selectedActionKeys = [...this.userActionKeys];
                this.localModuleCount = JSON.parse(JSON.stringify(this.moduleCount));
                this.$emit('setNewActionKeys', this.selectedActionKeys);
                this.onPermissionLoad();
            }
        },
        roleId: {
            immediate: true,
            handler(to) {
                if (to) {
                    this.getRoleDetails(to);
                } else {
                    this.$store.commit('permissions/SET_GROUPED_PERMISSIONS', undefined);
                    this.isLoadingPermissions = false;
                }
            }
        },
        // rolePermissions(value) {
        //     this.filterPermissions(value);
        // },
        moduleCount(value) {
            this.localModuleCount = JSON.parse(JSON.stringify(value));
            this.$emit('setNewModuleCount', this.localModuleCount);
        },
        userActionKeys(value) {
            if (value) {
                this.selectedActionKeys = [...value];
                this.$emit('setNewActionKeys', this.selectedActionKeys);
            }
        },
        permissions() {
            this.onPermissionLoad();
        }
    },
    methods: {
        // Show/hide panel
        collapsePanel(collapsed, value) {
            this.panel[value] = collapsed ? [0] : [];
        },
        // Check if given value is in array
        exists(arr, val) {
            return arr.indexOf(val) !== -1;
        },
        // Add/delete in array based on isChecked value
        modifyArray(isChecked, arr, value) {
            if (isChecked) {
                if (!this.exists(arr, value)) {
                    arr.push(value);
                }
            } else {
                const i = arr.indexOf(value);
                if (i > -1) {
                    arr.splice(i, 1);
                }
            }
        },
        // Show total selected / total items
        getCount(name) {
            let count = 0;
            let total = 0;

            if (this.localModuleCount.length != 0) {
                if (name in this.localModuleCount['count']) {
                    count = this.localModuleCount['count'][name] || 0;
                }

                if (name in this.localModuleCount['total']) {
                    total = this.localModuleCount['total'][name] || 0;
                }
            }

            return `${count}/${total}`;
        },
        // Get role details
        getRoleDetails(role_id) {
            this.$store.commit('roles/SET_ROLE', {});

            this.$store
                .dispatch('roles/findRole', {
                    id: role_id,
                    config: {
                        params: {
                            'appends[0]': 'permissions',
                            'appends[1]': 'role_name'
                        }
                    }
                })
                .then((response) => {
                    this.filterPermissions(response.data.permissions);
                });
        },
        //  Create action key array
        filterPermissions(permissions) {
            // Reset Action Keys
            this.newActionKeys = [];

            // Temporary fix for diff permission response
            if (Array.isArray(permissions)) {
                const userPermissions = [...new Set(permissions)];
                Object.values(userPermissions).forEach((value) => {
                    if (value) {
                        this.newActionKeys.push(value);
                    }
                });
            } else {
                Object.keys(permissions).forEach((value) => {
                    if (permissions[value]) {
                        this.newActionKeys.push(value);
                    }
                });
            }

            //this.selectedActionKeys = this.newActionKeys;
            this.$store.commit('permissions/SET_USER_ACTION_KEYS', this.newActionKeys);
            this.$emit('setNewActionKeys', this.selectedActionKeys);
            this.getPermissions();
        },
        // Get permissions
        getPermissions() {
            this.isLoadingPermissions = true;
            this.$store
                .dispatch('permissions/fetchPermissions', {
                    config: {
                        params: {
                            grouped: 0
                        }
                    }
                })
                .then(() => {
                    this.isLoadingPermissions = false;
                })
                .catch(() => {
                    this.isLoadingPermissions = false;
                });
        },
        // Get group and subgroup checked items
        onPermissionLoad() {
            this.selectedGroups = [];
            this.selectedSubGroups = [];
            for (const val of Object.values(this.permissions)) {
                // Check group count; Check if total = count
                if (this.localModuleCount['total'][val.group] == this.localModuleCount['count'][val.group]) {
                    if (!this.exists(this.selectedGroups, val.group)) {
                        this.selectedGroups.push(val.group);
                    }
                }

                // Check subgroup count; Check if total = count
                const subgroup = val.group + '-' + val.sub_group;
                if (this.localModuleCount['total'][subgroup] == this.localModuleCount['count'][subgroup]) {
                    if (!this.exists(this.selectedSubGroups, subgroup)) {
                        this.selectedSubGroups.push(subgroup);
                    }
                }
            }

            this.onPanelLoad();
        },
        // Show/hide panel based on count
        // @param {boolean} all if true, shows all panel
        onPanelLoad(all = true) {
            for (const panel of Object.keys(this.localModuleCount['total'])) {
                if (all) {
                    // Open all panels
                    this.collapsePanel(all, panel);
                } else {
                    // Open panel only if there are checked action keys under them
                    if (this.localModuleCount['count'][panel] > 0) {
                        this.collapsePanel(true, panel);
                    }
                }
            }
        },
        // On group check, determine subgroups and action keys to tick as well as their count/total
        toggleGroup(group) {
            const isChecked = this.exists(this.selectedGroups, group);
            const groups = Object.values(this.permissions).filter((value) => {
                return value.group == group;
            });

            for (const val of groups) {
                if (val.group == group) {
                    const sub_group = group + '-' + val.sub_group;
                    this.modifyArray(isChecked, this.selectedSubGroups, sub_group);
                    this.modifyArray(isChecked, this.selectedActionKeys, val.action_key);
                    this.collapsePanel(isChecked, sub_group);

                    this.localModuleCount['count'][sub_group] = !isChecked ? 0 : this.localModuleCount['total'][sub_group];
                }
            }

            this.localModuleCount['count'][group] = !isChecked ? 0 : this.localModuleCount['total'][group];
            this.collapsePanel(isChecked, group);
            this.$emit('setNewActionKeys', this.selectedActionKeys);
            this.$emit('setNewModuleCount', this.localModuleCount);
        },
        // On subgroup check, determine groups and action keys to tick as well as their count/total
        toggleSubGroup(group, subgroupName) {
            const subgroup = group + '-' + subgroupName;
            const isChecked = this.exists(this.selectedSubGroups, subgroup);
            let subgroupCount = 0;
            let groupCount = 0;

            const groups = Object.values(this.permissions).filter((value) => {
                return value.group == group;
            });

            for (const val of groups) {
                if (val.sub_group == subgroupName) {
                    this.modifyArray(isChecked, this.selectedActionKeys, val.action_key);
                    if (isChecked) subgroupCount++;
                }

                if (this.exists(this.selectedActionKeys, val.action_key)) {
                    groupCount++;
                }
            }

            this.localModuleCount['count'][subgroup] = subgroupCount;
            this.localModuleCount['count'][group] = groupCount;

            this.modifyArray(this.localModuleCount['count'][group] == this.localModuleCount['total'][group], this.selectedGroups, group);
            this.collapsePanel(this.localModuleCount['count'][subgroup] > 0, subgroup);
            // this.collapsePanel(this.localModuleCount['count'][group] > 0, group);
            this.$emit('setNewActionKeys', this.selectedActionKeys);
            this.$emit('setNewModuleCount', this.localModuleCount);
        },
        // On actionkey check, determine groups and subgroups to tick as well as their count/total
        onCheckActionKey(group, subgroupName) {
            const subgroup = group + '-' + subgroupName;
            let subgroupcount = 0;
            let groupcount = 0;

            const groups = Object.values(this.permissions).filter((value) => {
                return value.group == group;
            });

            for (const val of groups) {
                if (this.exists(this.selectedActionKeys, val.action_key)) {
                    if (val.sub_group == subgroupName) {
                        subgroupcount++;
                    }

                    groupcount++;
                }
            }

            this.localModuleCount['count'][group] = groupcount;
            this.localModuleCount['count'][subgroup] = subgroupcount;

            this.modifyArray(this.localModuleCount['count'][group] == this.localModuleCount['total'][group], this.selectedGroups, group);
            this.modifyArray(this.localModuleCount['count'][subgroup] == this.localModuleCount['total'][subgroup], this.selectedSubGroups, subgroup);
            // this.collapsePanel(subgroupcount > 0, subgroup);
            this.$emit('setNewActionKeys', this.selectedActionKeys);
            this.$emit('setNewModuleCount', this.localModuleCount);
        }
    }
};
</script>

<style lang="scss" scoped>
.permissions {
    min-height: 500px;
}

ul,
li {
    list-style: none;
    padding-left: 5px;
}

.view {
    pointer-events: none;
    user-select: none;
}

.v-expansion-panels--inset > .v-expansion-panel--active {
    max-width: unset;
}

.v-expansion-panel--active > .v-expansion-panel-header {
    min-height: unset;
}

.group-panel-header {
    &.v-expansion-panel-header,
    &.v-expansion-panel-header--active {
        padding: 10px 15px;
        background: #ffffff;
        box-shadow: 0px 1px 2px rgba(50, 50, 71, 0.08), 0px 0px 1px rgba(50, 50, 71, 0.2);
        border-radius: 6px;
    }
}

.subgroup-panel-header {
    &.v-expansion-panel-header,
    &.v-expansion-panel-header--active {
        padding: 0px 15px 0 10px;
    }
}

.v-expansion-panel-header:before {
    background: none !important;
    box-shadow: none !important;
}

::v-deep {
    .v-expansion-panel-content__wrap {
        padding-right: 0;
        padding-bottom: 0;
    }

    .v-label {
        color: #16192c !important;
    }
}
</style>
